import React, { useCallback, useMemo, useState } from "react";
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Platform,
  Linking,
  StyleSheet,
  Modal,
  Image,
  ScrollView,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import StandardNavigation from "../../../components/navigation/StandardNavigation";
import i18n from "../../../locales/i18n";
import { useOrders, Order } from "../../../hooks/useOrders";
import { useFocusEffect } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import Animated, { FadeInDown } from "react-native-reanimated";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { API_URL } from "../../../lib/config";
import WebOrdersScreen from "../../../components/web/WebOrdersScreen";

type FilterType = "all" | "pending" | "confirmed";

const FilterBar = ({
  active,
  onChange,
  counts,
  isRTL,
}: {
  active: FilterType;
  onChange: (f: FilterType) => void;
  counts: { all: number; pending: number; confirmed: number };
  isRTL: boolean;
}) => {
  const filters: {
    key: FilterType;
    label: string;
    color: string;
    bg: string;
  }[] = [
    { key: "all", label: i18n.t("filterAll"), color: "#004E6D", bg: "#e0f2fe" },
    {
      key: "pending",
      label: i18n.t("filterPending"),
      color: "#d97706",
      bg: "#fffbeb",
    },
    {
      key: "confirmed",
      label: i18n.t("filterConfirmed"),
      color: "#059669",
      bg: "#ecfdf5",
    },
  ];

  return (
    <View style={[styles.filterBar, isRTL && styles.rowRTL]}>
      {filters.map((f) => {
        const isActive = active === f.key;
        return (
          <TouchableOpacity
            key={f.key}
            onPress={() => onChange(f.key)}
            style={[
              styles.filterChip,
              {
                backgroundColor: isActive ? f.bg : "#f3f4f6",
                borderColor: isActive ? f.color : "transparent",
              },
            ]}
          >
            <Text
              style={[
                styles.filterChipText,
                { color: isActive ? f.color : "#6b7280" },
              ]}
            >
              {f.label} ({counts[f.key]})
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
};

const OrderDetailModal = ({
  order,
  visible,
  onClose,
  onConfirm,
  onCancel,
  onDelete,
  isRTL,
}: {
  order: Order | null;
  visible: boolean;
  onClose: () => void;
  onConfirm: (id: number) => void;
  onCancel: (id: number) => void;
  onDelete: (id: number) => void;
  isRTL: boolean;
}) => {
  const insets = useSafeAreaInsets();
  if (!order) return null;

  const productPhoto = order.Product?.photo
    ? order.Product.photo.startsWith("http")
      ? order.Product.photo
      : `${API_URL}${order.Product.photo}`
    : null;

  const total = order.Product
    ? order.Product.price_sell * order.quantity
    : null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={styles.modalOverlay}>
        <View
          style={[
            styles.modalContent,
            { paddingBottom: Math.max(insets.bottom, 24) },
          ]}
        >
          <ScrollView showsVerticalScrollIndicator={false}>
            {/* Header */}
            <View style={[styles.modalHeader, isRTL && styles.rowRTL]}>
              <Text style={styles.modalTitle}>{i18n.t("orderDetails")}</Text>
              <TouchableOpacity onPress={onClose} style={styles.modalCloseBtn}>
                <Ionicons name="close" size={24} color="#374151" />
              </TouchableOpacity>
            </View>

            {/* Status */}
            <View
              style={[
                styles.statusBadge,
                order.confirmed ? styles.confirmedBadge : styles.pendingBadge,
                { alignSelf: "center", marginBottom: 20 },
              ]}
            >
              <Ionicons
                name={order.confirmed ? "checkmark-circle" : "time-outline"}
                size={16}
                color={order.confirmed ? "#059669" : "#d97706"}
              />
              <Text
                style={[
                  styles.statusText,
                  order.confirmed ? styles.confirmedText : styles.pendingText,
                ]}
              >
                {order.confirmed
                  ? i18n.t("orderConfirmed")
                  : i18n.t("orderPending")}
              </Text>
            </View>

            {/* Product Info */}
            {order.Product && (
              <View style={styles.productSection}>
                {productPhoto && (
                  <Image
                    source={{ uri: productPhoto }}
                    style={styles.productImage}
                    resizeMode="cover"
                  />
                )}
                <Text style={[styles.productName, isRTL && styles.textRTL]}>
                  {order.Product.name}
                </Text>
                <View style={styles.priceRow}>
                  <View style={styles.priceItem}>
                    <Text style={styles.priceLabel}>{i18n.t("unitPrice")}</Text>
                    <Text style={styles.priceValue}>
                      {order.Product.price_sell} DA
                    </Text>
                  </View>
                  <View style={styles.priceDivider} />
                  <View style={styles.priceItem}>
                    <Text style={styles.priceLabel}>{i18n.t("quantity")}</Text>
                    <Text style={styles.priceValue}>x{order.quantity}</Text>
                  </View>
                  {total !== null && (
                    <>
                      <View style={styles.priceDivider} />
                      <View style={styles.priceItem}>
                        <Text style={styles.priceLabel}>
                          {i18n.t("totalPrice")}
                        </Text>
                        <Text
                          style={[
                            styles.priceValue,
                            { color: "#004E6D", fontWeight: "700" },
                          ]}
                        >
                          {total} DA
                        </Text>
                      </View>
                    </>
                  )}
                </View>
              </View>
            )}

            {/* Client Details */}
            <View style={styles.detailSection}>
              <DetailRow
                icon="person-outline"
                label={i18n.t("clientName")}
                value={order.client_fullname}
                isRTL={isRTL}
              />
              <DetailRow
                icon="call-outline"
                label={i18n.t("clientPhone")}
                value={order.client_phone}
                isRTL={isRTL}
                isPhone
              />
              <DetailRow
                icon="location-outline"
                label={i18n.t("clientLocation")}
                value={order.location}
                isRTL={isRTL}
              />
              <DetailRow
                icon="calendar-outline"
                label={i18n.t("orderDate")}
                value={new Date(order.createdAt).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
                isRTL={isRTL}
              />
            </View>
          </ScrollView>

          {/* Actions - outside ScrollView so always visible */}
          <View style={[styles.modalActions, isRTL && styles.rowRTL]}>
            {!order.confirmed ? (
              <TouchableOpacity
                style={[styles.confirmBtn, { flex: 1 }]}
                onPress={() => {
                  onConfirm(order.id_order);
                  onClose();
                }}
              >
                <Ionicons name="checkmark" size={18} color="#fff" />
                <Text style={styles.confirmBtnText}>
                  {i18n.t("confirmOrder")}
                </Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                style={[styles.cancelBtn, { flex: 1 }]}
                onPress={() => {
                  onCancel(order.id_order);
                  onClose();
                }}
              >
                <Ionicons name="close" size={18} color="#d97706" />
                <Text style={styles.cancelBtnText}>
                  {i18n.t("cancelOrder")}
                </Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={styles.deleteBtn}
              onPress={() => {
                onClose();
                setTimeout(() => {
                  Alert.alert(i18n.t("deleteOrder"), "", [
                    { text: i18n.t("close"), style: "cancel" },
                    {
                      text: i18n.t("deleteOrder"),
                      style: "destructive",
                      onPress: () => onDelete(order.id_order),
                    },
                  ]);
                }, 300);
              }}
            >
              <Ionicons name="trash-outline" size={18} color="#ef4444" />
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const DetailRow = ({
  icon,
  label,
  value,
  isRTL,
  isPhone,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  value: string;
  isRTL: boolean;
  isPhone?: boolean;
}) => (
  <View style={[styles.detailRow, isRTL && styles.rowRTL]}>
    <Ionicons name={icon} size={18} color="#004E6D" />
    <View
      style={{ flex: 1, marginLeft: isRTL ? 0 : 8, marginRight: isRTL ? 8 : 0 }}
    >
      <Text style={[styles.detailLabel, isRTL && styles.textRTL]}>{label}</Text>
      {isPhone ? (
        <TouchableOpacity onPress={() => Linking.openURL(`tel:${value}`)}>
          <Text style={[styles.detailValuePhone, isRTL && styles.textRTL]}>
            {value}
          </Text>
        </TouchableOpacity>
      ) : (
        <Text style={[styles.detailValue, isRTL && styles.textRTL]}>
          {value}
        </Text>
      )}
    </View>
  </View>
);

const OrderCard = ({
  order,
  index,
  onConfirm,
  onCancel,
  onDelete,
  onPress,
  isRTL,
}: {
  order: Order;
  index: number;
  onConfirm: (id: number) => void;
  onCancel: (id: number) => void;
  onDelete: (id: number) => void;
  onPress: (order: Order) => void;
  isRTL: boolean;
}) => {
  const productPhoto = order.Product?.photo
    ? order.Product.photo.startsWith("http")
      ? order.Product.photo
      : `${API_URL}${order.Product.photo}`
    : null;

  return (
    <Animated.View entering={FadeInDown.duration(300).delay(index * 60)}>
      <TouchableOpacity
        activeOpacity={0.7}
        onPress={() => onPress(order)}
        style={styles.card}
      >
        <View style={[styles.cardTopRow, isRTL && styles.rowRTL]}>
          {/* Product thumbnail */}
          {productPhoto ? (
            <Image
              source={{ uri: productPhoto }}
              style={styles.thumb}
              resizeMode="cover"
            />
          ) : (
            <View style={[styles.thumb, styles.thumbPlaceholder]}>
              <Ionicons name="cube-outline" size={24} color="#9ca3af" />
            </View>
          )}

          {/* Info */}
          <View
            style={{
              flex: 1,
              marginLeft: isRTL ? 0 : 12,
              marginRight: isRTL ? 12 : 0,
            }}
          >
            <View
              style={[
                {
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                },
                isRTL && styles.rowRTL,
              ]}
            >
              <Text
                style={[styles.clientName, isRTL && styles.textRTL]}
                numberOfLines={1}
              >
                {order.client_fullname}
              </Text>
              <View
                style={[
                  styles.statusBadgeSm,
                  order.confirmed ? styles.confirmedBadge : styles.pendingBadge,
                ]}
              >
                <Text
                  style={[
                    styles.statusTextSm,
                    order.confirmed ? styles.confirmedText : styles.pendingText,
                  ]}
                >
                  {order.confirmed
                    ? i18n.t("orderConfirmed")
                    : i18n.t("orderPending")}
                </Text>
              </View>
            </View>

            {order.Product && (
              <Text
                style={[styles.productLabel, isRTL && styles.textRTL]}
                numberOfLines={1}
              >
                {order.Product.name} × {order.quantity}
              </Text>
            )}

            <View style={[styles.cardMeta, isRTL && styles.rowRTL]}>
              <View
                style={{ flexDirection: "row", alignItems: "center", gap: 4 }}
              >
                <Ionicons name="location-outline" size={13} color="#9ca3af" />
                <Text style={styles.metaText} numberOfLines={1}>
                  {order.location}
                </Text>
              </View>
              <Text style={styles.metaText}>
                {new Date(order.createdAt).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </Text>
            </View>
          </View>
        </View>

        {/* Quick actions */}
        <View style={[styles.cardActions, isRTL && styles.rowRTL]}>
          <TouchableOpacity
            style={styles.callBtn}
            onPress={(e) => {
              e.stopPropagation;
              Linking.openURL(`tel:${order.client_phone}`);
            }}
          >
            <Ionicons name="call-outline" size={16} color="#004E6D" />
            <Text style={styles.callBtnText}>{order.client_phone}</Text>
          </TouchableOpacity>

          {!order.confirmed ? (
            <TouchableOpacity
              style={styles.confirmBtnSm}
              onPress={() => onConfirm(order.id_order)}
            >
              <Ionicons name="checkmark" size={16} color="#fff" />
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              style={styles.cancelBtnSm}
              onPress={() => onCancel(order.id_order)}
            >
              <Ionicons name="close" size={16} color="#d97706" />
            </TouchableOpacity>
          )}
        </View>
      </TouchableOpacity>
    </Animated.View>
  );
};

const OrdersScreen = () => {
  if (Platform.OS === "web") {
    return <WebOrdersScreen />;
  }

  const { locale } = useSelector((state: RootState) => state.app);
  const isRTL = locale === "ar";
  const [filter, setFilter] = useState<FilterType>("all");
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const {
    orders,
    isLoading,
    isRefreshing,
    fetchOrders,
    confirmOrder,
    cancelOrder,
    deleteOrder,
  } = useOrders();

  useFocusEffect(
    useCallback(() => {
      fetchOrders();
    }, [fetchOrders]),
  );

  const counts = useMemo(
    () => ({
      all: orders.length,
      pending: orders.filter((o) => !o.confirmed).length,
      confirmed: orders.filter((o) => o.confirmed).length,
    }),
    [orders],
  );

  const filtered = useMemo(() => {
    if (filter === "pending") return orders.filter((o) => !o.confirmed);
    if (filter === "confirmed") return orders.filter((o) => o.confirmed);
    return orders;
  }, [orders, filter]);

  const renderEmpty = () => {
    if (isLoading) return null;
    return (
      <View style={styles.emptyContainer}>
        <Ionicons name="receipt-outline" size={80} color="#d1d5db" />
        <Text style={styles.emptyTitle}>{i18n.t("noOrders")}</Text>
        <Text style={styles.emptyDesc}>{i18n.t("noOrdersDesc")}</Text>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <StandardNavigation title={i18n.t("orders")} />

      <FilterBar
        active={filter}
        onChange={setFilter}
        counts={counts}
        isRTL={isRTL}
      />

      {isLoading && orders.length === 0 ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#004E6D" />
        </View>
      ) : (
        <FlatList
          data={filtered}
          keyExtractor={(item) => String(item.id_order)}
          renderItem={({ item, index }) => (
            <OrderCard
              order={item}
              index={index}
              onConfirm={confirmOrder}
              onCancel={cancelOrder}
              onDelete={deleteOrder}
              onPress={setSelectedOrder}
              isRTL={isRTL}
            />
          )}
          contentContainerStyle={
            filtered.length === 0 ? styles.emptyList : styles.list
          }
          ListEmptyComponent={renderEmpty}
          refreshing={isRefreshing}
          onRefresh={() => fetchOrders(true)}
          showsVerticalScrollIndicator={false}
        />
      )}

      <OrderDetailModal
        order={selectedOrder}
        visible={!!selectedOrder}
        onClose={() => setSelectedOrder(null)}
        onConfirm={confirmOrder}
        onCancel={cancelOrder}
        onDelete={deleteOrder}
        isRTL={isRTL}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#f9fafb",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  list: {
    padding: 16,
    paddingBottom: 32,
  },
  emptyList: {
    flex: 1,
  },
  // Filter bar
  filterBar: {
    flexDirection: "row",
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 8,
  },
  filterChip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1.5,
  },
  filterChipText: {
    fontSize: 13,
    fontWeight: "600",
  },
  // Card
  card: {
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 14,
    marginBottom: 10,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 6,
    elevation: 2,
  },
  cardTopRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  thumb: {
    width: 52,
    height: 52,
    borderRadius: 12,
  },
  thumbPlaceholder: {
    backgroundColor: "#f3f4f6",
    justifyContent: "center",
    alignItems: "center",
  },
  clientName: {
    fontSize: 16,
    fontWeight: "700",
    color: "#1f2937",
    flex: 1,
    marginRight: 8,
  },
  productLabel: {
    fontSize: 13,
    color: "#6b7280",
    marginTop: 2,
  },
  cardMeta: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 6,
  },
  metaText: {
    fontSize: 12,
    color: "#9ca3af",
  },
  cardActions: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#f3f4f6",
    gap: 8,
  },
  callBtn: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 10,
    backgroundColor: "#f0f9ff",
  },
  callBtnText: {
    fontSize: 13,
    color: "#004E6D",
    fontWeight: "500",
  },
  confirmBtnSm: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: "#059669",
    justifyContent: "center",
    alignItems: "center",
  },
  cancelBtnSm: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: "#fffbeb",
    borderWidth: 1,
    borderColor: "#fde68a",
    justifyContent: "center",
    alignItems: "center",
  },
  // Status badges
  statusBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    gap: 4,
  },
  statusBadgeSm: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  confirmedBadge: {
    backgroundColor: "#ecfdf5",
  },
  pendingBadge: {
    backgroundColor: "#fffbeb",
  },
  statusText: {
    fontSize: 13,
    fontWeight: "600",
  },
  statusTextSm: {
    fontSize: 11,
    fontWeight: "600",
  },
  confirmedText: {
    color: "#059669",
  },
  pendingText: {
    color: "#d97706",
  },
  rowRTL: {
    flexDirection: "row-reverse",
  },
  textRTL: {
    textAlign: "right",
  },
  // Buttons
  confirmBtn: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#059669",
    paddingVertical: 12,
    borderRadius: 12,
    gap: 6,
  },
  confirmBtnText: {
    color: "#fff",
    fontSize: 15,
    fontWeight: "600",
  },
  cancelBtn: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fffbeb",
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#fde68a",
    gap: 6,
  },
  cancelBtnText: {
    color: "#d97706",
    fontSize: 15,
    fontWeight: "600",
  },
  deleteBtn: {
    padding: 12,
    borderRadius: 12,
    backgroundColor: "#fef2f2",
    borderWidth: 1,
    borderColor: "#fecaca",
  },
  // Empty state
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 40,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: "700",
    color: "#374151",
    marginTop: 16,
  },
  emptyDesc: {
    fontSize: 14,
    color: "#9ca3af",
    marginTop: 8,
    textAlign: "center",
  },
  // Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "flex-end",
  },
  modalContent: {
    backgroundColor: "#fff",
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    paddingHorizontal: 20,
    paddingTop: 20,
    maxHeight: "85%",
  },
  modalHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "700",
    color: "#1f2937",
  },
  modalCloseBtn: {
    padding: 4,
  },
  modalActions: {
    flexDirection: "row",
    gap: 10,
    marginTop: 20,
  },
  // Product section
  productSection: {
    backgroundColor: "#f9fafb",
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    alignItems: "center",
  },
  productImage: {
    width: 100,
    height: 100,
    borderRadius: 14,
    marginBottom: 12,
  },
  productName: {
    fontSize: 17,
    fontWeight: "700",
    color: "#1f2937",
    textAlign: "center",
    marginBottom: 12,
  },
  priceRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 0,
  },
  priceItem: {
    alignItems: "center",
    paddingHorizontal: 14,
  },
  priceLabel: {
    fontSize: 11,
    color: "#9ca3af",
    marginBottom: 2,
  },
  priceValue: {
    fontSize: 16,
    fontWeight: "600",
    color: "#374151",
  },
  priceDivider: {
    width: 1,
    height: 30,
    backgroundColor: "#e5e7eb",
  },
  // Detail section
  detailSection: {
    gap: 14,
  },
  detailRow: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  detailLabel: {
    fontSize: 12,
    color: "#9ca3af",
  },
  detailValue: {
    fontSize: 15,
    color: "#374151",
    fontWeight: "500",
    marginTop: 1,
  },
  detailValuePhone: {
    fontSize: 15,
    color: "#004E6D",
    fontWeight: "500",
    textDecorationLine: "underline",
    marginTop: 1,
  },
});

export default OrdersScreen;
